import { NumberHelper } from "~/helpers/numberHelper";
import { InstanceHelper } from "./instanceHelper";
import {NumberKeys} from "~/interfaces/generalInterfaces"

export let ArrayHelper = {
  remove: function<T>(array: T[], value): T[] {
    for (let i = array.length - 1; i >=0; i--) {
      if (array[i] === value) array.splice(i, 1);
    }
    return array;
  },
  removeFirst: function<T>(array: T[], value): T[] {
    const i = array.indexOf(value);
    if (i !== -1) array.splice(i, 1);
    return array;
  },
  add: function<T>(array: T[], value: T, position?: number): T[] {
    if (array.includes(value)) {
      if (NumberHelper.isInteger(position)) {
        this.move(array, value, position);
      } // don't do anything if included in array but no position provided
    } else {
      position = NumberHelper.isInteger(position) ? position : array.length
      array.splice(position, 0, value);
    }
    return array;
  },
  toggle: function<T>(array: T[], value): T[] {
    if (array.includes(value)) {
      return this.remove(array, value);
    } else {
      return this.add(array, value);
    }
  },
  moveIndex: function<T>(array: T[], oldIndex, newIndex): T[] {
    if (newIndex >= array.length) {
      let k = newIndex - array.length + 1;
      while (k--) {
        array.push(undefined);
      }
    }
    array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
    return array;
  },
  move: function<T>(array: T[], value, newIndex: number = array.length): T[] {
    const oldIndex = array.indexOf(value);
    return this.moveIndex(array, oldIndex, newIndex);
  },
  moveUp: function<T>(array: T[], value): T[] {
    const oldIndex = array.indexOf(value);
    return this.moveIndex(array, oldIndex, oldIndex - 1);
  },
  moveDown: function<T>(array: T[], value): T[] {
    const oldIndex = array.indexOf(value);
    return this.moveIndex(array, oldIndex, oldIndex + 1);
  },
  any: function<T>(array: T[], values?): boolean {
    if (values) {
      return array.some(arrVal => values.includes(arrVal));
    } else !!array.length
  },
  gatherByProp<T>(root, array: T[], prop: string): T[] {
    const newItems = root[prop];
    array.push(...newItems);
    newItems.forEach(item => {
      this.gatherByProp(item, array, prop);
    });
    return array;
  },
  sortByDate: function<T>(array: T[], prop, direction = 'desc'): T[] {
    const sort = direction === 'asc' ? 1 : -1;
    array.sort((a, b) => {
      let aDate = null;
      let bDate = null;
      if (prop) {
        aDate = new Date(a[prop]);
        bDate = new Date(b[prop]);
      } else {
        aDate = new Date(a.toString());
        bDate = new Date(b.toString());
      }
      return (aDate.getTime() - bDate.getTime()) * sort;
    });
    return array;
  },
  sortByString<T>(array: T[], prop: string, direction: 'asc' | 'desc' = 'asc', caseInsensitive: boolean = false): T[] {
    let sortTransform = null;
    if (caseInsensitive) {
      sortTransform = (value: string) => {
        return value?.toLowerCase();
      };
    } else {
      sortTransform = (value: string) => {
        return value;
      };
    }
    return array.sort((a, b): number => {
      const aTransform = sortTransform(a[prop]);
      const bTransform = sortTransform(b[prop]);
      if (aTransform === bTransform) {
        return 0;
      }
      if (direction === 'asc') {
        return aTransform < bTransform ? -1 : 1;
      } else {
        return aTransform < bTransform ? 1 : -1;
      }
    });
  },

  sortByNumber<T>(array: T[], prop: NumberKeys<T>, direction: 'asc' | 'desc' = 'asc'): T[] {
    return array.sort((a, b): number => {
      if (a[prop] === b[prop]) {
        return 0;
      }
      if (direction === 'asc') {
        return a[prop] < b[prop] ? -1 : 1;
      } else {
        return a[prop] < b[prop] ? 1 : -1;
      }
    });
  },
  toSentence<T = string>(array: T[], separator: string = ', ', lastSeparator: string = 'and', builder?: ((item: T) => string)): string {
    if (array === null || array === undefined) return ''

    const getString = (item: T): string => {
      if (InstanceHelper.isFunction(builder)) {
        return builder(item);
      } else if (InstanceHelper.isString(item)) {
        return item;
      } else {
        // use + operator to convert null/undefined to string instead of throwing an error
        return "" + item;
      }
    };

    switch (array.length) {
      case 0:
        return '';
      case 1:
        return getString(array[0]);
      case 2:
        return `${getString(array[0])} ${lastSeparator} ${getString(array[1])}`;
      default:
        const lastIndex = array.length - 1;
        const items = array.map(getString);
        return `${items.slice(0, lastIndex).join(separator)}${separator} ${lastSeparator} ${items[lastIndex]}`;
    }
  },
  prepForRails<T>(array: T[], {emptyFormat}: {emptyFormat?: 'string' | 'array'} = {}): T[] | [''] | "[]" {
    emptyFormat = emptyFormat ?? 'array'
    if (array.length === 0) {
      return emptyFormat === 'array' ? [''] : "[]";
    } else {
      return array.slice();
    }
  },
  isPresent(array: any[]): boolean {
    return array.length > 0;
  },
  /**
   * Looks specifically at whether every index in array is +undefined+
   */
  isEmpty<T>(array: T): boolean {
    return this.uniq(array).length === 1 && array[0] === undefined
  },
  clone<T>(array: T[]): T[] {
    return array.slice();
  },
  updatePositionsByProp<T>(array: T[], movedItem, newPosition: number, prop: string): void {
    array
      .filter(item => item !== movedItem)
      .sort((a, b) => a[prop] - b[prop])
      .forEach((item, i) => {
        item[prop] = i;
        if (item[prop] >= newPosition) {
          item[prop] += 1;
        }
      });
  },
  equals<T>(a: T[], b: T[]): boolean {
    if (a === b) return true;
    if (a === null || b === null) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  },
  last<T>(array: T[], callback?: (value: T, index: number, array: T[]) => any): T {
    if (callback) array = array.filter(callback);
    return array[array.length - 1];
  },
  /**
   *
   * @param recurse the key on which to recurse through
   */
  find<T>(array: T[], callback: (value: T, index: number, array: T[]) => boolean, {recurse}: {recurse?: string} = {}): T {
    if (!Array.isArray(array)) return undefined
    let found = array.find(callback)
    if (found || !recurse) return found

    for (let item of array) {
      found = this.find(item[recurse], callback, {recurse});
      if (found) return found;
    }
    return found
  },
  /**
   * @param array {Array} items in which to iterate over
   * @param recurse {string} the key on which to recurse through, depth first
   * @param callback {Function} what to run for each item
   * @param parent {Object} when recursing, parent object of item
   */
  each<T>(array: T[], callback: (value: T, index: number, array: T[], parent: T) => void, {recurse}: {recurse?: string} = {}, parent?: T): void {
    if (!Array.isArray(array)) return undefined

    array.forEach((item, i, arr) => {
      callback(item, i, arr, parent)
      this.each(item[recurse], callback, {recurse}, item)
    })
  },
  // find all elements in a that are not included in ...b
  diff<T>(a: T[], ...b: Array<T[]>): T[] {
    return a.filter(x => !b.some(bb => bb.includes(x)))
  },
  // if arg is not an array, then wrap it in one
  wrap<T>(a: T | T[]): T[] {
    if (InstanceHelper.isNil(a)) return []

    return Array.isArray(a) ? a : [a]
  },
  // remove duplicates from array
  uniq<T>(a: T | T[]): T[] {
    return Array.from(new Set(this.wrap(a)))
  },
  compact<T>(a: T[]): T[] {
    return a.filter(item => item !== null && item !== undefined)
  },
};
